/**
 * Thẻ «Tình hình kinh doanh» (thiết kế 2026-10-31-tinh-hinh-kinh-doanh-design.md, CEO
 * chốt 31/10/2026): ô số của nút đang xem (so cùng kỳ tháng trước — CEO 1a 04/11/2026), ba biểu đồ, bảng đi sâu
 * miền → đội → tiểu đội → người, khung chi tiết một người. Máy chủ đã cắt phạm vi
 * (4a; sale thường chỉ nhận tiểu đội + chính mình — 9a), màn chỉ vẽ.
 */
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronRight } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { formatVND, formatIntVN } from "@/lib/format";
import { Sheet, SheetContent } from "@/ui/sheet";
import { type SalesOrgView, type SalesTeamOverview, type SalesTeamOverviewMetrics, type SalesTeamOverviewNode, useSalesTeamOverview } from "../api";
import { monthText } from "../labels";
import { NODE_KIND_LABEL, averageOrder, childNodes, combineNodes, pathTo, prevCompare, rootNodes, targetPercent } from "../overview";
import { type PeopleSortKey, peopleBySquad } from "../overviewPeople";
import { OverviewCharts } from "./OverviewCharts";
import { OverviewPeople } from "./OverviewPeople";
import { MetricTiles } from "./OverviewTiles";
import { PersonDetail } from "./OverviewPerson";
import { PersonMark, SquadMark } from "./marks";

type Props = { month: string; /** Cơ cấu HRM — để biết ai là Sales, ai là CSKH khi xếp hạng (3b). */ org?: SalesOrgView | undefined };

export function OverviewTab({ month, org }: Props) {
  const q = useSalesTeamOverview(month);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);

  if (q.isError) return <QueryErrorNote error={q.error} fallback="Không tải được tình hình kinh doanh" />;
  if (q.isLoading || !q.data) return <p className="text-sm text-muted">Đang tải…</p>;
  const o = q.data;
  if (o.beforeScheme) {
    return (
      <p className="rounded-md border border-border p-4 text-sm text-muted">
        Số đội bán hàng tính từ tháng {monthText(o.schemeStart.slice(0, 7))}. Chọn tháng từ đó trở đi để xem.
      </p>
    );
  }
  return (
    <OverviewBody
      key={month}
      o={o}
      month={month}
      focusId={focusId}
      onFocus={setFocusId}
      personId={personId}
      onPerson={setPersonId}
      org={org}
    />
  );
}

type BodyProps = {
  o: SalesTeamOverview;
  month: string;
  focusId: string | null;
  onFocus: (id: string | null) => void;
  personId: string | null;
  onPerson: (id: string | null) => void;
  org?: SalesOrgView | undefined;
};

function OverviewBody({ o, month, focusId, onFocus, personId, onPerson, org }: BodyProps) {
  const [mode, setMode] = useState<"units" | "people">("units");
  const [sortKey, setSortKey] = useState<PeopleSortKey>("realRevenue");
  const groups = useMemo(() => peopleBySquad(o, focusId, sortKey, org), [o, focusId, sortKey, org]);
  const roots = useMemo(() => rootNodes(o), [o]);
  const byId = useMemo(() => new Map(o.nodes.map((n) => [n.id, n])), [o]);
  const focused = focusId ? byId.get(focusId) : undefined;
  // Không chọn gì: một gốc thì xem gốc đó; nhiều gốc rời nhau thì xem gộp.
  const view = focused ?? combineNodes(roots, "Phần bạn được xem");
  if (!view) {
    return <p className="text-sm text-muted">Chưa có số nào trong phạm vi bạn được xem tháng {monthText(month)}.</p>;
  }
  const rows = focused ? childNodes(o, focused.id) : roots.length === 1 && roots[0] ? childNodes(o, roots[0].id) : roots;
  const trail = focused ? pathTo(o, focused.id) : [];
  const person = personId ? byId.get(personId) : undefined;
  // Nút chỉ có khi bảng còn đơn vị bên dưới và người xem không bị giới hạn ở chính mình.
  const canPeople = !o.limited && !rows.every((r) => r.kind === "person") && groups.length > 0;
  const showPeople = canPeople && mode === "people";

  return (
    <div className="space-y-6">
      <nav aria-label="Đang xem" className="flex flex-wrap items-center gap-1 text-sm">
        <button type="button" className="min-h-11 rounded px-1 font-medium underline-offset-2 hover:underline" onClick={() => onFocus(null)}>
          {roots.length === 1 && roots[0] ? roots[0].name : "Phần bạn được xem"}
        </button>
        {trail
          .filter((n) => !(roots.length === 1 && n.id === roots[0]?.id))
          .map((n) => (
            <span key={n.id} className="flex items-center gap-1">
              <ChevronRight aria-hidden className="h-3 w-3 text-muted" />
              <button type="button" className="min-h-11 rounded px-1 underline-offset-2 hover:underline" onClick={() => onFocus(n.id)}>
                {n.name}
              </button>
            </span>
          ))}
        {o.limited ? (
          <span className="ml-2 text-xs text-muted">Bạn xem được số của mình và tổng tiểu đội.</span>
        ) : null}
      </nav>

      <MetricTiles m={view.metrics} prev={view.prev} prevFull={view.prevFull} prevUntil={o.prevUntil} isPerson={view.kind === "person"} />

      <OverviewCharts node={view} month={month} schemeStart={o.schemeStart} />

      <section aria-labelledby="bang-di-sau" className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="bang-di-sau" className="text-base font-semibold">
            {showPeople ? "Từng người theo tiểu đội" : rows.every((r) => r.kind === "person") ? "Từng người" : "Từng đơn vị"}
          </h3>
          {/* «Xem từng người» (CEO 1a, 31/10/2026): sale thường chỉ thấy mình nên không có nút này. */}
          {canPeople ? (
            <div role="group" aria-label="Cách xem bảng" className="inline-flex rounded-md border border-border p-0.5 text-sm">
              {(
                [
                  ["units", "Xem theo đơn vị"],
                  ["people", "Xem từng người"],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={mode === k}
                  className={mode === k ? "min-h-11 rounded bg-primary px-3 text-white" : "min-h-11 rounded px-3 text-muted hover:text-foreground"}
                  onClick={() => setMode(k)}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {showPeople ? (
          <OverviewPeople
            groups={groups}
            sortKey={sortKey}
            onSort={setSortKey}
            current={o.currentMonth}
            compare={prevCompare(o.prevUntil)}
            onPerson={(n) => onPerson(n.id)}
          />
        ) : (
          <DrillTable rows={rows} current={o.currentMonth} onOpen={(n) => (n.kind === "person" ? onPerson(n.id) : onFocus(n.id))} />
        )}
      </section>

      <Sheet open={!!person} onOpenChange={(open) => !open && onPerson(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {person ? <PersonDetail node={person} month={month} prevUntil={o.prevUntil} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** Ảnh / logo cạnh mọi tên (CEO 30/10/2026): người → ảnh nhân sự; tiểu đội → logo; đội, miền → chữ cái đầu. */
function NodeMark({ node }: { node: SalesTeamOverviewNode }) {
  if (node.kind === "person") return <PersonMark staffId={node.id} name={node.name} size={24} />;
  return <SquadMark squadId={node.kind === "squad" ? node.id : undefined} name={node.name} size={24} />;
}

function DrillTable({
  rows,
  current,
  onOpen,
}: {
  rows: SalesTeamOverviewNode[];
  current: boolean;
  onOpen: (n: SalesTeamOverviewNode) => void;
}) {
  const columns = useMemo<ColumnDef<SalesTeamOverviewNode>[]>(() => {
    const money = (key: keyof SalesTeamOverviewMetrics, header: string, priority: "primary" | "secondary") =>
      ({
        id: key,
        header,
        cell: ({ row }) => <span className="tabular-nums">{formatVND(row.original.metrics[key] as number)}</span>,
        meta: { mobilePriority: priority, mobileLabel: header },
      }) satisfies ColumnDef<SalesTeamOverviewNode>;
    const cols: ColumnDef<SalesTeamOverviewNode>[] = [
      {
        id: "name",
        header: "Tên",
        cell: ({ row }) => (
          <button type="button" className="inline-flex min-h-11 items-center text-left font-medium underline-offset-2 hover:underline" onClick={() => onOpen(row.original)}>
            <NodeMark node={row.original} />
            <span className="ml-1 text-xs font-normal text-muted">{NODE_KIND_LABEL[row.original.kind] ?? ""}</span>
          </button>
        ),
        meta: { mobilePriority: "primary" },
      },
      money("realRevenue", "Thực tế", "primary"),
      {
        id: "target",
        header: "Đạt mục tiêu",
        cell: ({ row }) => {
          if (row.original.kind === "person") return <span className="text-muted">—</span>;
          const p = targetPercent(row.original.metrics);
          return <span className="tabular-nums">{p == null ? "Chưa đặt" : `${p}%`}</span>;
        },
        meta: { mobilePriority: "secondary", mobileLabel: "Đạt mục tiêu" },
      },
      money("expected", "Dự kiến", "secondary"),
      {
        id: "orders",
        header: "Đơn",
        cell: ({ row }) => <span className="tabular-nums">{formatIntVN(row.original.metrics.orders)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Đơn" },
      },
      {
        id: "avg",
        header: "TB một đơn",
        cell: ({ row }) => {
          const a = averageOrder(row.original.metrics);
          return <span className="tabular-nums">{a == null ? "—" : formatVND(a)}</span>;
        },
        meta: { mobilePriority: "secondary", mobileLabel: "TB một đơn" },
      },
      money("collected", "Đã đưa", "secondary"),
      money("upsell", "Bán thêm", "secondary"),
    ];
    if (current) {
      cols.push({
        id: "overdue",
        header: "Quá hẹn",
        cell: ({ row }) => <span className="tabular-nums">{formatIntVN(row.original.metrics.overdueLeads ?? 0)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Khách quá hẹn" },
      });
    }
    return cols;
  }, [current, onOpen]);

  return (
    <DataTable
      columns={columns}
      data={rows}
      mobileVariant="stack"
      onRowClick={onOpen}
      emptyMessage="Chưa có đơn vị hay người nào bên dưới."
    />
  );
}
